import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { getMessages, getMessagesAroundMessage } from '../MessagesServices';
import { debounce } from '../../../Utils/debounce';
import { performanceMonitor } from '../../../Utils/performanceMonitor';
import useOptimizedMessages from './useOptimizedMessages';

const useMessageFetching = (selectedConversation, handleError, markMessageAsSeen) => {
    const {
        messages,
        replaceMessages,
        appendMessages,
        prependMessages, // NEW
        addMessage,
        updateMessage,
        deleteMessage
    } = useOptimizedMessages([]);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isFetchingMoreTop, setIsFetchingMoreTop] = useState(false);
    const [isFetchingMoreBottom, setIsFetchingMoreBottom] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [hasMoreNewer, setHasMoreNewer] = useState(false);
    const [nextCursor, setNextCursor] = useState(null); // For loading older messages
    const [prevCursor, setPrevCursor] = useState(null); // For loading newer messages
    const messageRefs = useRef({});
    const perPage = 10;
    // Synchronous flag to prevent scroll-triggered fetches during jump-to-message operations
    const isJumpingRef = useRef(false);
    // Synchronous flag to prevent concurrent bottom fetches (state updates are async and can be stale)
    const isFetchingBottomRef = useRef(false);

    const fetchMessages = useCallback(async ({ cursor = null, shouldAppend = false, direction = 'older', onScrollEnd = () => { } } = {}) => {
        if (!selectedConversation) return;

        return performanceMonitor.trackRender('useMessageFetching.fetchMessages', async () => {
            const isInitial = !cursor && !shouldAppend;
            if (isInitial) {
                setIsInitialLoading(true);
            }

            // Set appropriate loading state
            setLoading(true);

            try {
                // Determine params based on direction
                // direction 'older' = scroll UP = fetch older history (after)
                // direction 'newer' = scroll DOWN = fetch newer history (before)
                const params = {
                    conversation_id: selectedConversation.id,
                    size: perPage,
                };

                if (direction === 'older') {
                    params.after = cursor; // Older messages
                } else {
                    params.before = cursor; // Newer messages
                }

                const { data, metadata } = await getMessages(params);
                onScrollEnd();

                if (direction === 'older') {
                    // Update older cursor (for scrolling up)
                    const next = metadata.next_cursor;
                    setNextCursor(next);
                    setHasMore(!!next);

                    if (shouldAppend) {
                        appendMessages(data);
                    } else {
                        replaceMessages(data);
                        // Mark the most recent message as seen when initially loading
                        if (isInitial && data.length > 0 && markMessageAsSeen) {
                            markMessageAsSeen(data[0]);
                        }
                    }
                } else {
                    // Update newer cursor (for scrolling down to even newer messages)
                    // prev_cursor points to the next page of newer messages
                    // Do NOT fall back to next_cursor - that's for older messages!
                    setPrevCursor(metadata.prev_cursor || null);
                    setHasMoreNewer(!!metadata.prev_cursor || metadata.has_prev || false);

                    if (shouldAppend) {
                        prependMessages(data);
                    }
                }

                // Return metadata so caller can know if we reached the end
                return {
                    hasMoreOlder: !!metadata.next_cursor || metadata.has_next || false,
                    hasMoreNewer: !!metadata.prev_cursor || metadata.has_prev || false,
                    nextCursor: metadata.next_cursor,
                    prevCursor: metadata.prev_cursor
                };

            } catch (error) {
                handleError(error);
                if (direction === 'older') setHasMore(false);
                else setHasMoreNewer(false);
                return null;
            } finally {
                setLoading(false);
                if (isInitial) {
                    setIsInitialLoading(false);
                }
            }
        });
    }, [selectedConversation, handleError, appendMessages, prependMessages, replaceMessages, markMessageAsSeen]);

    const fetchMessagesAroundMessage = useCallback(async (messageId) => {
        if (!selectedConversation) return;

        // Set jumping flag synchronously to prevent scroll-triggered fetches
        // This must be a ref (not state) because stateRef reads it synchronously in the debounced handler
        isJumpingRef.current = true;

        return performanceMonitor.trackRender('useMessageFetching.fetchMessagesAroundMessage', async () => {
            // Clear messages and show loading
            replaceMessages([]);
            setIsInitialLoading(true);
            setLoading(true);

            try {
                // Fetch messages around the target using the same pattern as Conversations.js:
                // - after=messageId (exclusive) for older messages
                // - until=messageId (inclusive) for newer messages + target
                const contextSize = 10;

                const [olderResponse, newerResponse] = await Promise.all([
                    // Get older messages AFTER the target (exclusive - doesn't include target)
                    getMessages({
                        conversation_id: selectedConversation.id,
                        after: messageId,
                        size: contextSize
                    }),
                    // Get newer messages + target (until is inclusive - includes target)
                    getMessages({
                        conversation_id: selectedConversation.id,
                        until: messageId,
                        size: contextSize
                    })
                ]);

                // Combine: newerResponse contains target (until is inclusive)
                // Order: [Newest...Target, Older...Oldest] (DESC order)
                const newerMessages = newerResponse.data || [];  // Contains target
                const olderMessages = olderResponse.data || [];  // Doesn't contain target

                const combinedMessages = [...newerMessages, ...olderMessages];

                // Set messages
                replaceMessages(combinedMessages);

                // Set cursors for bidirectional scrolling
                // next_cursor from older response = for loading even older messages (scroll up)
                // prev_cursor from newer response = for loading even newer messages (scroll down)
                setNextCursor(olderResponse.metadata?.next_cursor || null);
                setPrevCursor(newerResponse.metadata?.prev_cursor || null);
                setHasMore(!!olderResponse.metadata?.next_cursor || olderResponse.metadata?.has_next || false);
                setHasMoreNewer(!!newerResponse.metadata?.prev_cursor || newerResponse.metadata?.has_prev || false);

                return messageId;
            } catch (error) {
                handleError(error);
                setHasMore(false);
                setHasMoreNewer(false);
            } finally {
                setLoading(false);
                setIsInitialLoading(false);
                // Clear jumping flag after a delay to allow scroll position to stabilize
                // Without this delay, the scroll event may fire before the list settles
                setTimeout(() => {
                    isJumpingRef.current = false;
                }, 300);
            }
        });
    }, [selectedConversation, handleError, replaceMessages]);

    // Store current state in refs to avoid stale closures in the debounced scroll handler
    const stateRef = useRef();
    stateRef.current = {
        loading,
        hasMore,
        hasMoreNewer,
        isFetchingMoreTop,
        isFetchingMoreBottom,
        fetchMessages,
        nextCursor,
        prevCursor
    };

    // Debounced scroll handler to prevent excessive API calls
    // Empty dependency array is intentional: we read fresh state from stateRef.current
    const debouncedFetchMore = useMemo(
        () => debounce((container) => {
            // Skip scroll-triggered fetches during jump-to-message operations
            if (isJumpingRef.current) {
                return;
            }

            const {
                loading: currentLoading,
                hasMore: currentHasMore,
                hasMoreNewer: currentHasMoreNewer,
                isFetchingMoreTop: currentIsFetchingTop,
                isFetchingMoreBottom: currentIsFetchingBottom,
                fetchMessages: currentFetchMessages,
                nextCursor: currentNextCursor,
                prevCursor: currentPrevCursor
            } = stateRef.current;

            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;

            // Top Scroll (Load OLDER messages)
            if (scrollTop === 0 && !currentLoading && !currentIsFetchingTop && currentHasMore) {
                let prevScrollTop = 0;
                let prevScrollHeight = 0;
                setIsFetchingMoreTop(true);
                currentFetchMessages({
                    cursor: currentNextCursor,
                    shouldAppend: true,
                    direction: 'older',
                    onScrollEnd: () => {
                        prevScrollTop = container.scrollTop;
                        prevScrollHeight = container.scrollHeight;
                    }
                }).then(() => {
                    requestAnimationFrame(() => {
                        const newScrollHeight = container.scrollHeight;
                        const delta = newScrollHeight - prevScrollHeight;
                        // Preserve visual position after adding older messages to top
                        container.scrollTop = prevScrollTop + delta;
                    });
                }).finally(() => {
                    setIsFetchingMoreTop(false);
                });
            }

            // Bottom Scroll (Load NEWER messages)
            // Use a threshold (e.g., 50px from bottom)
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
            // Use synchronous ref to prevent race conditions (state updates are async)

            if (isNearBottom && !currentLoading && currentHasMoreNewer && !isFetchingBottomRef.current) {
                // Set synchronous flag immediately to prevent concurrent fetches
                isFetchingBottomRef.current = true;
                // Determine if we should prevent auto-scroll or maintain position
                // Typically when loading newer messages at bottom, we want to just append them.
                // If user is stuck at bottom, they should stay at new bottom.

                setIsFetchingMoreBottom(true);

                // Capture scroll state before fetch for position restoration
                let prevScrollTop = container.scrollTop;
                let prevScrollHeight = container.scrollHeight;

                currentFetchMessages({
                    cursor: currentPrevCursor,
                    shouldAppend: true,
                    direction: 'newer',
                    onScrollEnd: () => {
                        prevScrollTop = container.scrollTop;
                        prevScrollHeight = container.scrollHeight;
                    }
                }).then((result) => {
                    requestAnimationFrame(() => {
                        // Check if we reached the very end of history (live mode)
                        const hasMoreNewer = result?.hasMoreNewer;

                        if (!hasMoreNewer) {
                            // If we reached the end, scroll to bottom to show the latest messages
                            // This is safe because hasMoreNewer is false, so no more fetches will trigger
                            const newScrollHeight = container.scrollHeight;
                            container.scrollTop = newScrollHeight;
                        } else {
                            // Otherwise, keep the user at their previous visual position
                            // (scroll position stays relative to existing content, not the new bottom)
                            container.scrollTop = prevScrollTop;
                        }

                        // Clear synchronous flag AFTER scroll position is restored
                        // This ensures no scroll events can trigger new fetches until we're done
                        setTimeout(() => {
                            isFetchingBottomRef.current = false;
                        }, 50);
                    });
                }).finally(() => {
                    setIsFetchingMoreBottom(false);
                });
            }
        }, 100),
        []
    );

    const handleScroll = useCallback((event) => {
        const container = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = container;

        // Check for Top (Older) OR Bottom (Newer)
        const isAtTop = scrollTop === 0;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

        if (isAtTop || isNearBottom) {
            debouncedFetchMore(container);
        }
    }, [debouncedFetchMore]);

    // Only refetch when conversation ID changes, not when other properties like is_writing change
    const conversationId = selectedConversation?.id;
    useEffect(() => {
        if (conversationId) {
            replaceMessages([]);
            setNextCursor(null);
            setPrevCursor(null);
            setHasMore(true);
            setHasMoreNewer(false);
            fetchMessages({ cursor: null, shouldAppend: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    return {
        messages,
        loading,
        hasMore,
        hasMoreNewer,
        messageRefs,
        handleScroll,
        addMessage,
        updateMessage,
        deleteMessage,
        isInitialLoading,
        isFetchingMoreTop,
        isFetchingMoreBottom,
        fetchMessagesAroundMessage
    };
};

export default useMessageFetching;